<script setup>
import { useAlert } from 'dashboard/composables';
import AddCanned from './AddCanned.vue';
import EditCanned from './EditCanned.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Table from 'dashboard/components-next/table/Table.vue';
import EmptyState from 'dashboard/components-next/empty-state/EmptyState.vue';

defineOptions({
  name: 'CannedResponseSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showAddPopup = ref(false);
const loading = ref({});
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});
const cannedResponseAPI = ref({ message: '' });

const sortOrder = ref('asc');
const records = computed(() =>
  getters.getSortedCannedResponses.value(sortOrder.value)
);
const uiFlags = computed(() => getters.getUIFlags.value);

const deleteConfirmText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.short_code} ? `;
});

const toggleSort = () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
};

const fetchCannedResponses = async () => {
  try {
    await store.dispatch('getCannedResponse');
  } catch (error) {
    // Ignore Error
  }
};

onMounted(() => {
  fetchCannedResponses();
});

const showAlertMessage = message => {
  loading[activeResponse.value.id] = false;
  activeResponse.value = {};
  cannedResponseAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  activeResponse.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteCannedResponse = async id => {
  try {
    await store.dispatch('deleteCannedResponse', id);
    showAlertMessage(t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteCannedResponse(activeResponse.value.id);
};

// Columns for the canonical Table primitive.
// The "Short code" column header carries a custom sort toggle rendered via a
// header-cell slot — the underlying data and behavior are unchanged.
const columns = computed(() => [
  { key: 'short_code', label: t('CANNED_MGMT.LIST.TABLE_HEADER.SHORT_CODE') },
  { key: 'content',    label: t('CANNED_MGMT.LIST.TABLE_HEADER.CONTENT') },
  {
    key: 'actions',
    label: t('CANNED_MGMT.LIST.TABLE_HEADER.ACTIONS'),
    align: 'right',
    width: '8rem',
  },
]);
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('CANNED_MGMT.HEADER')"
      :description="$t('CANNED_MGMT.DESCRIPTION')"
      :link-text="$t('CANNED_MGMT.LEARN_MORE')"
      feature-name="canned_responses"
    >
      <template #actions>
        <Button
          icon="i-lucide-circle-plus"
          :label="$t('CANNED_MGMT.HEADER_BTN_TXT')"
          @click="openAddPopup"
        />
      </template>
    </BaseSettingsHeader>

    <div class="mt-6 flex-1">
      <Table
        :columns="columns"
        :rows="records"
        :row-key="row => row.short_code"
        :loading="uiFlags.fetchingList"
        :loading-rows="6"
      >
        <!-- Custom header for the sortable "Short code" column -->
        <template #header>
          <tr>
            <th
              class="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-n-slate-11 border-b border-n-weak whitespace-nowrap"
            >
              <button
                class="flex items-center gap-1 p-0 cursor-pointer text-[11px] font-semibold uppercase tracking-[0.04em] text-n-slate-11 hover:text-n-slate-12"
                @click="toggleSort"
              >
                <span>{{ $t('CANNED_MGMT.LIST.TABLE_HEADER.SHORT_CODE') }}</span>
                <fluent-icon
                  class="size-3"
                  :icon="sortOrder === 'desc' ? 'chevron-up' : 'chevron-down'"
                />
              </button>
            </th>
            <th
              class="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-n-slate-11 border-b border-n-weak whitespace-nowrap"
            >
              {{ $t('CANNED_MGMT.LIST.TABLE_HEADER.CONTENT') }}
            </th>
            <th
              class="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.04em] text-n-slate-11 border-b border-n-weak whitespace-nowrap"
              style="width: 8rem"
            >
              {{ $t('CANNED_MGMT.LIST.TABLE_HEADER.ACTIONS') }}
            </th>
          </tr>
        </template>

        <template #cell-short_code="{ row }">
          <span class="font-medium text-n-slate-12 truncate max-w-xs block" :title="row.short_code">
            {{ row.short_code }}
          </span>
        </template>
        <template #cell-content="{ row }">
          <span class="md:break-all whitespace-normal">{{ row.content }}</span>
        </template>
        <template #cell-actions="{ row }">
          <div class="flex justify-end gap-1">
            <Button
              v-tooltip.top="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
              icon="i-lucide-pen"
              slate
              xs
              faded
              @click="openEditPopup(row)"
            />
            <Button
              v-tooltip.top="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              :is-loading="loading[row.id]"
              @click="openDeletePopup(row)"
            />
          </div>
        </template>
        <template #empty>
          <EmptyState
            icon="i-lucide-message-square-quote"
            :title="$t('CANNED_MGMT.LIST.404')"
            variant="compact"
          />
        </template>
      </Table>
    </div>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddCanned :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditCanned
        v-if="showEditPopup"
        :id="activeResponse.id"
        :edshort-code="activeResponse.short_code"
        :edcontent="activeResponse.content"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>
